import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {Price, ProductDto} from "../../../api";
import {CheckoutFeatureListComponent} from "../checkout-feature-list/checkout-feature-list.component";
import {DecimalPipe, NgClass} from "@angular/common";
import {BicBadgeComponent, BicButtonComponent} from "@buildinginformationcloud/bic-ui-components";

/**
 * Die V44-Spalten `headlineValue`/`headlineLabel` der Preiskarte.
 *
 * <p>Als eigene Sicht auf `ProductDto`, weil der generierte Client sie erst nach dem naechsten
 * `api-client-generation.sh` kennt – das generierte Modell wird nicht von Hand editiert.
 */
type ProductWithHeadline = ProductDto & { headlineValue?: string; headlineLabel?: string };

@Component({
  selector: 'app-shop-price-card',
  templateUrl: './checkout-price-card.component.html',
  styleUrls: ['./checkout-price-card.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    DecimalPipe,
    BicBadgeComponent,
    BicButtonComponent,
    CheckoutFeatureListComponent,
  ]
})
export class CheckoutPriceCardComponent {

  /** Fehlt bei Plänen ohne Stripe-Preis (Enterprise). */
  @Input() price?: Price;
  @Input() activePrice!: Price;
  @Input() addOn: Price | null = null;
  @Input() isAddOn: boolean = false;
  @Input() isSpecial: boolean = false;
  @Input() active: boolean = false;
  @Input() product: ProductDto = {};
  @Output() checkoutClicked: EventEmitter<Price> = new EventEmitter<Price>();

  isRecurring() {
    return this.productPrice?.recurring != undefined;
  }

  get productPrice() {
    return this.isAddOn ? this.addOn : this.price;
  }

  get isActive() {
    return this.isAddOn ? !this.active : this.active;
  }

  get isTestphaseAndHasActivePrice() {
    return (this.product.isTrial ?? false) && this.activePrice != null;
  }

  /** Name aus der DB, sonst der Produktname aus Stripe. */
  get name() {
    return this.product.name ?? this.productPrice?.productObject?.name ?? '';
  }

  /**
   * Die grosse Zahl auf der Karte – seit V44 die Anzahl der Pruefungen, nicht mehr die Credits.
   *
   * <p>Fallback auf `credits`, damit eine Karte ohne gepflegte Spalten (eine Umgebung ohne den
   * neuen Seed, ein per Stripe-Webhook neu angelegtes Produkt) weiter dasselbe zeigt wie vorher.
   */
  get headlineValue(): string {
    const product = this.product as ProductWithHeadline;
    return product.headlineValue ?? String(this.product.credits ?? 0);
  }

  /** Text unter der grossen Zahl; ohne gepflegte Spalte wie bisher "Credits". */
  get headlineLabel(): string {
    return (this.product as ProductWithHeadline).headlineLabel ?? 'Credits';
  }

  /** Preistext, wenn kein Betrag angezeigt wird: "individuell" bzw. "Kostenlos". */
  get priceHeadline(): string | null {
    if (this.product.contactOnly) {
      return this.product.priceLabel ?? null;
    }
    return this.productPrice?.unitAmount == 0 ? 'Kostenlos' : null;
  }

  get ctaLabel() {
    if (this.product.ctaLabel) {
      return this.product.ctaLabel;
    }
    return this.isAddOn && !this.isActive ? 'Hinzufügen'
      : this.isAddOn && this.isActive ? 'Nicht verfügbar'
        : this.isActive ? 'Aktiv' : 'Jetzt starten';
  }

  onCta() {
    if (this.product.contactOnly) {
      window.location.href = `mailto:${this.product.ctaMailto}`;
      return;
    }
    if (!this.isActive && this.price) {
      this.checkoutClicked.emit(this.price);
    }
  }
}
